import { createSelector, Dictionary } from '@reduxjs/toolkit';
import cloneDeep from 'lodash.clonedeep';
import flatMap from 'lodash.flatmap';
import groupBy from 'lodash.groupby';
import uniqBy from 'lodash.uniqby';

import { RootState } from '../../redux/root-reducer';
import { isValidTicketAvailable } from '../../utils/tickets';
import { SeriesTicketType } from '../event-details/event-detail-types';
import {
  Answer,
  QuestionTypeWithBankId,
} from '../registration-form/custom-form-factory-types';
import { SeriesSliceState } from './series-reducer';

export const getAllEvents = createSelector(
  (state: RootState) => state.series,
  series => series.eventsWidgets.data,
);

export const isEventSeriesDataLoading = createSelector(
  (state: RootState) => state.series,
  series => series.eventsWidgets.loading,
);

export const isEventTicketsDataLoading = createSelector(
  (state: RootState) => state.series,
  series => series.tickets.loading,
);

export const getSeriesDataError = createSelector(
  (state: RootState) => state.series,
  series => series.eventsWidgets.error,
);

export const getSelectedEvents = createSelector(
  (state: RootState) => state.series.selectedEvents,
  (state: RootState) => state.series.eventsWidgets.data,
  (checkedEvents, events) => {
    return events.filter(
      event => checkedEvents[event.event.externalId]?.isSelected,
    );
  },
);

export const getSelectionState = createSelector(
  (state: RootState) => state.series.selectedEvents,
  (state: RootState) => state.series.eventsWidgets.data,
  (checkedEvents, events) => {
    const selectableEvents = events.filter(event => event.tickets?.length > 0);
    if (selectableEvents.length <= 1) return null;

    const selectedCount = selectableEvents.filter(
      event => checkedEvents[event.event.externalId]?.isSelected,
    ).length;

    if (selectedCount === 0) return 'none';
    if (selectedCount === selectableEvents.length) return 'all';
    return 'some';
  },
);

export const getAllSeriesEventWidgets = createSelector(
  (state: RootState) => state.series,
  series => series.eventsWidgets.data,
);

export const getAllSeriesTickets = createSelector(
  (state: RootState) => state.series,
  series => series.tickets.data,
);

export const getSeriesSelectedTickets = createSelector(
  (state: RootState) => state.series,
  series => series.selectedTicketIds,
);

export const getSeriesSelectedEvents = createSelector(
  (state: RootState) => state.series,
  series => series.selectedEvents,
);

export const isCartEmpty = createSelector(
  (state: RootState) => state.seriesCart,
  seriesCart => Object.keys(seriesCart.selectedTicketOrderSummary).length === 0,
);

export const getTicketsByEventExternalId = createSelector(
  [getAllSeriesEventWidgets, (_, eventExternalId: string) => eventExternalId],
  (eventWidgets, eventExternalId) => {
    const widget = eventWidgets.find(
      widget => widget.event.externalId === eventExternalId,
    );

    const tickets = widget === undefined ? [] : [...widget.tickets];

    // Sort tickets: valid first
    return tickets.sort((a, b) => {
      const isValidA = isValidTicketAvailable(a);
      const isValidB = isValidTicketAvailable(b);

      // If validity is different, valid tickets come first
      if (isValidA !== isValidB) {
        return isValidA ? -1 : 1;
      }
      return 0;
    });
  },
);

export const getValidTicketsByEventExternalId = createSelector(
  [getAllSeriesEventWidgets, (_, eventExternalId: string) => eventExternalId],
  (eventWidgets, eventExternalId) => {
    const widget = eventWidgets.find(
      widget => widget.event.externalId === eventExternalId,
    );

    const tickets = widget === undefined ? [] : widget.tickets;
    return tickets.filter(ticket => isValidTicketAvailable(ticket));
  },
);

export const getSelectedTicketByEventExternalId = createSelector(
  [
    getAllSeriesTickets,
    getSeriesSelectedTickets,
    (_, eventExternalId: string) => eventExternalId,
  ],
  (tickets, selectedTickets, eventExternalId): SeriesTicketType | null => {
    const ticketExternalId = selectedTickets[eventExternalId];

    if (!ticketExternalId) {
      return null;
    }

    return (
      (tickets[eventExternalId] || []).find(
        ticket => ticket.externalId === ticketExternalId,
      ) ?? null
    );
  },
);

export const getSeriesQuestionsLoading = createSelector(
  (state: RootState) => state.series,
  series => series.questions.loading,
);

export const getSeriesQuestions = createSelector(
  (state: RootState) => state.series,
  (series: SeriesSliceState) => {
    const bankQuestionIdMap = new Map<string, number>();

    const allQuestionsWithEventIds = flatMap(
      series.questions.data,
      (questions: QuestionTypeWithBankId[], eventExternalId: string) =>
        questions.map(question => {
          const bankQuestionId = question.bank_question_id;
          if (bankQuestionId) {
            bankQuestionIdMap.set(
              bankQuestionId,
              (bankQuestionIdMap.get(bankQuestionId) || 0) + 1,
            );
          }
          return {
            ...question,
            eventExternalId,
          };
        }),
    );

    // Add sharedBankQuestion flag to all questions with duplicate bank_question_id
    const questionsWithSharedFlag = allQuestionsWithEventIds.map(question => {
      const bankQuestionId = question.bank_question_id;
      if (bankQuestionId && (bankQuestionIdMap.get(bankQuestionId) ?? 0) > 1) {
        return {
          ...question,
          sharedBankQuestion: true,
        };
      }
      return question;
    });

    // Filter out any duplicate questions based on bank_question_id
    const uniqueQuestions = uniqBy(questionsWithSharedFlag, 'bank_question_id');

    // Re-group the unique questions back by eventExternalId
    const groupedQuestions = groupBy(uniqueQuestions, 'eventExternalId');
    return groupedQuestions;
  },
);

export const getSeriesQuestionsError = createSelector(
  (state: RootState) => state.series,
  series => series.questions.error,
);

export const getSeriesAnswers = createSelector(
  (state: RootState) => state.series,
  series => series.questions.answers,
);

export const getSavedSeriesAnswers = createSelector(
  (state: RootState) => state.series.questions.answers,
  getSeriesQuestions,
  (seriesAnswers, seriesQuestions) => {
    if (!seriesQuestions || !seriesAnswers) return {};

    const flattenedAnswers = flatMap(
      seriesAnswers,
      (answers, eventExternalId) =>
        answers.map(answer => ({
          ...answer,
          eventExternalId,
        })),
    );

    return Object.entries(seriesQuestions).reduce<
      Record<string, Nullable<Answer[]>>
    >((acc, [eventExternalId, questions]) => {
      acc[eventExternalId] = questions
        ?.map(question => {
          const answer = flattenedAnswers.find(
            a => a.bank_question_id === question.bank_question_id,
          );
          if (answer) {
            return {
              ...answer,
              question_id: question.id,
              eventExternalId: question.eventExternalId,
            };
          }
          return null;
        })
        .filter(Boolean) as Answer[];
      return acc;
    }, {});
  },
);

export const getSeriesUserDetailsAnswers = createSelector(
  (state: RootState) => state.series,
  series => series.questions.userDetails,
);

export const isPaymentRequired = createSelector(
  (state: RootState) => state.seriesCart,
  seriesCart => seriesCart.seriesCartTotal > 0,
);

export const getCurrency = createSelector(
  (state: RootState) => state.series,
  series => {
    return series.eventsWidgets?.data[0]?.event?.currency;
  },
);

export const getUpdatedSeriesAnswers = createSelector(
  (state: RootState) => state.series,
  series => {
    const { questions } = series;
    const { data, answers } = questions;

    const flattenedQuestions = flatMap(data, (questions, eventExternalId) =>
      questions.map(question => ({
        ...question,
        event_external_id: eventExternalId,
      })),
    );

    const questionsGroupedByBankId = groupBy(
      flattenedQuestions,
      'bank_question_id',
    );

    const updatedAnswers = cloneDeep(answers);

    Object.entries(questionsGroupedByBankId).forEach(([_, questions]) => {
      if (questions.length > 1) {
        //  There is a group of questions with matching bank_question_ids so we need to find the matching answer for this group
        const matchingAnswer = flatMap(Object.values(answers)).find(answer =>
          questions.some(question => question.id === answer.question_id),
        );

        if (matchingAnswer) {
          // Add the matching answer for each question in the group under the corresponding event_external_id
          questions.forEach(question => {
            const eventExternalId = question.event_external_id;
            if (!updatedAnswers[eventExternalId]) {
              updatedAnswers[eventExternalId] = [];
            }

            const existingAnswerIndex = updatedAnswers[
              eventExternalId
            ].findIndex(answer => answer.question_id === question.id);

            if (existingAnswerIndex === -1) {
              updatedAnswers[eventExternalId].push({
                ...matchingAnswer,
                question_id: question.id,
              });
            }
          });
        }
      }
    });

    return updatedAnswers;
  },
);

export const getErroredEvent = createSelector(
  (state: RootState) => state.series.erroredEventIds,
  (_: any, eventId: string) => eventId,
  (
    erroredEventIds: Record<
      string,
      { isErrored: boolean; errorMessages?: string[] }
    >,
    eventId: string,
  ) => erroredEventIds[eventId],
);

export const getIsErroredEventPresentInCart = createSelector(
  (state: RootState) => state.series,
  series => {
    return Object.keys(series.selectedEvents)
      .filter(eventId => series.selectedEvents[eventId]?.isSelected)
      .some(eventId => series.erroredEventIds[eventId]?.isErrored);
  },
);

export const getActiveSeriesName = createSelector(
  (state: RootState) => state.series,
  series => {
    const activeSeries = series.activeSeries;
    if (!activeSeries) return '';
    const eventSeriesName = series.eventSeries[activeSeries];
    if (!eventSeriesName) return '';
    return eventSeriesName;
  },
);
